import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import { useRef, useState, useEffect } from 'react';
import { v4 as uuid4 } from 'uuid';
import useStorage from '../../../../hooks/use-storage/use-storage';
import Storage from '../../../../services/storage';
import { ANIMATION_STATUS, DEFAULT_ANIMATION_DURATION, ANIMATE_ON_MOUNT, RUN_ALWAYS, RUN_ONCE_EVERY_VISIT } from '../../animation.constants';
var PRE_ANIMATING = ANIMATION_STATUS.PRE_ANIMATING,
  ANIMATING = ANIMATION_STATUS.ANIMATING,
  NO_ANIMATION = ANIMATION_STATUS.NO_ANIMATION,
  SERVER_SIDE = ANIMATION_STATUS.SERVER_SIDE;
export default function useAnimation() {
  var transitions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$duration = _ref.duration,
    duration = _ref$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$duration,
    _ref$on = _ref.on,
    on = _ref$on === void 0 ? ANIMATE_ON_MOUNT : _ref$on,
    _ref$runPolicy = _ref.runPolicy,
    runPolicy = _ref$runPolicy === void 0 ? RUN_ALWAYS : _ref$runPolicy,
    _ref$animationKey = _ref.animationKey,
    animationKey = _ref$animationKey === void 0 ? uuid4() : _ref$animationKey;
  var timeoutId = useRef();
  var runPolicyRef = useRef(null);
  if (runPolicyRef.current === null) {
    if (runPolicy === RUN_ALWAYS) {
      runPolicyRef.current = '';
    } else {
      runPolicyRef.current = animationKey;
    }
  }
  var INITIAL_STATE = {
    status: NO_ANIMATION,
    prevStatus: NO_ANIMATION,
    cycle: 0,
    transition: transitions[0]
  };
  var _useState = useState(_objectSpread(_objectSpread({}, INITIAL_STATE), {}, {
      status: SERVER_SIDE,
      prevStatus: SERVER_SIDE
    })),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  var _useStorage = useStorage(runPolicyRef.current, false, {
      storageMechanism: Storage.SESSION_STORAGE
    }),
    _useStorage2 = _slicedToArray(_useStorage, 3),
    hasRunBefore = _useStorage2[0],
    setStorageValue = _useStorage2[1],
    storageLoaded = _useStorage2[2];
  function isReady() {
    return storageLoaded || runPolicy === RUN_ALWAYS;
  }
  function changeStateStatus(targetStatus) {
    var nextCycle = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (targetStatus === NO_ANIMATION && nextCycle) {
      setState(function (previousState) {
        var previousCycle = previousState.cycle;
        var newCycle = previousCycle + 1;
        if (newCycle >= transitions.length) {
          return _objectSpread(_objectSpread({}, INITIAL_STATE), {}, {
            prevStatus: previousState.status
          });
        }
        return {
          status: PRE_ANIMATING,
          prevStatus: previousState.status,
          cycle: newCycle,
          transition: transitions[newCycle]
        };
      });
    } else {
      setState(function (previousState) {
        return _objectSpread(_objectSpread({}, previousState), {}, {
          status: targetStatus,
          prevStatus: previousState.status
        });
      });
    }
  }
  useEffect(function () {
    if (on !== ANIMATE_ON_MOUNT) {
      changeStateStatus(NO_ANIMATION, false);
    }
    return function () {
      return clearTimeout(timeoutId.current);
    };
  }, []);
  function endAnimation() {
    clearTimeout(timeoutId.current);
    setState(INITIAL_STATE);
  }
  useEffect(function () {
    var cycle = state.cycle,
      status = state.status;
    if (status === NO_ANIMATION) {
      return;
    }
    if (cycle >= transitions.length) {
      endAnimation();
    }
    if (status === PRE_ANIMATING) {
      timeoutId.current = setTimeout(function () {
        return changeStateStatus(ANIMATING);
      }, 0);
    } else if (status === ANIMATING) {
      timeoutId.current = setTimeout(function () {
        return changeStateStatus(NO_ANIMATION, true);
      }, duration);
    }
  }, [state]);
  function startAnimation() {
    if (!isReady()) {
      return;
    }
    if (storageLoaded && hasRunBefore && runPolicy === RUN_ONCE_EVERY_VISIT) {
      changeStateStatus(NO_ANIMATION, true);
      return;
    }
    if (runPolicy === RUN_ONCE_EVERY_VISIT) {
      setStorageValue(true);
    }
    endAnimation();
    timeoutId.current = setTimeout(function () {
      return changeStateStatus(PRE_ANIMATING);
    }, 0);
  }
  return {
    isReady: isReady(),
    state: state,
    startAnimation: startAnimation,
    endAnimation: endAnimation
  };
}